import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { BookOpen, Clock, User, ChevronLeft, ChevronRight, Send } from 'lucide-react';

const categories = ['All', 'UI/UX', 'Development', 'AI Tools', 'Career'];

const blogPosts = [
    {
        id: 1,
        category: 'Career',
        title: 'UI/UX vs Graphic Design: Which Career in 2026?',
        excerpt: 'Confused between UI/UX and graphic design? Here\u2019s a detailed comparison of skills, salaries, and job prospects for 2026.',
        author: 'Priya Sharma',
        date: 'Jul 15, 2026',
        readTime: '7 min read',
    },
    {
        id: 2,
        category: 'AI Tools',
        title: 'Best AI Tools for Designers 2026',
        excerpt: 'From Figma plugins to generative AI, discover the tools that are reshaping how designers work this year.',
        author: 'Arjun Mehta',
        date: 'Jul 12, 2026',
        readTime: '5 min read',
    },
    {
        id: 3,
        category: 'Development',
        title: 'How to Build Your First App with AI',
        excerpt: 'A beginner-friendly guide to building a full-stack application using AI-assisted coding tools and modern frameworks.',
        author: 'Rahul Verma',
        date: 'Jul 8, 2026',
        readTime: '10 min read',
    },
    {
        id: 4,
        category: 'Career',
        title: 'Salary Guide: UI/UX Designer in India',
        excerpt: 'Explore salary ranges across experience levels, cities, and industries. Learn how to negotiate your worth in 2026.',
        author: 'Priya Sharma',
        date: 'Jul 5, 2026',
        readTime: '6 min read',
    },
    {
        id: 5,
        category: 'UI/UX',
        title: 'Figma vs Sketch: The Definitive Guide',
        excerpt: 'We break down the pros, cons, and hidden features of both tools to help you pick the right one for your workflow.',
        author: 'Arjun Mehta',
        date: 'Jul 1, 2026',
        readTime: '8 min read',
    },
    {
        id: 6,
        category: 'Career',
        title: 'How to Start Freelancing on Upwork',
        excerpt: 'From creating a winning profile to landing your first client, this step-by-step guide covers everything you need.',
        author: 'Rahul Verma',
        date: 'Jun 28, 2026',
        readTime: '9 min read',
    },
];

const Blog = () => {
    const [activeCategory, setActiveCategory] = useState('All');
    const [email, setEmail] = useState('');

    const filteredPosts = activeCategory === 'All'
        ? blogPosts
        : blogPosts.filter((post) => post.category === activeCategory);

    const handleSubscribe = (e) => {
        e.preventDefault();
        setEmail('');
    };

    return (
        <>
            <Helmet>
                <title>Blog & Resources | PyrusMedia</title>
                <meta name="description" content="Expert guides, tool reviews, and career advice for designers and developers from the PyrusMedia team." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">Insights & Resources</h1>
                    <p className="text-xl text-gray-500 max-w-3xl mx-auto">
                        Expert guides, tool reviews, and career advice from the PyrusMedia team.
                    </p>
                </div>
            </section>

            {/* Category Filter */}
            <Section className="bg-white pt-8 pb-4">
                <div className="flex flex-wrap justify-center gap-3">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border ${
                                activeCategory === cat
                                    ? 'bg-[#bebd19] text-gray-900 border-[#bebd19]'
                                    : 'bg-gray-100 text-gray-500 border-gray-200 hover:border-[#bebd19] hover:text-gray-900'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </Section>

            {/* Blog Grid */}
            <Section className="bg-gray-50 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredPosts.map((post, index) => (
                        <motion.article
                            key={post.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#bebd19] transition-all duration-300 flex flex-col"
                        >
                            {/* Image Placeholder */}
                            <div className="h-48 bg-gray-100 flex items-center justify-center border-b border-gray-200">
                                <BookOpen className="w-12 h-12 text-gray-300 group-hover:text-[#bebd19] transition-colors" />
                            </div>

                            <div className="p-6 flex flex-col flex-grow">
                                <span className="text-xs font-bold text-[#bebd19] uppercase tracking-wider mb-2">
                                    {post.category}
                                </span>
                                <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#bebd19] transition-colors line-clamp-2">
                                    {post.title}
                                </h3>
                                <p className="text-gray-500 text-sm mb-4 line-clamp-2 flex-grow">
                                    {post.excerpt}
                                </p>
                                <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                                    <span className="flex items-center gap-1.5">
                                        <User size={14} /> {post.author}
                                    </span>
                                    <span>{post.date}</span>
                                    <span className="flex items-center gap-1.5">
                                        <Clock size={14} /> {post.readTime}
                                    </span>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {filteredPosts.length === 0 && (
                    <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 border-dashed">
                        <p className="text-gray-500 text-lg">No articles in this category yet. Check back soon!</p>
                    </div>
                )}
            </Section>

            {/* Newsletter */}
            <Section className="bg-white py-20">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Get Weekly Insights</h2>
                    <p className="text-gray-500 mb-8">
                        Join 2,000+ designers and developers who get our best articles delivered every Thursday.
                    </p>
                    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            className="flex-grow px-5 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#bebd19] transition-colors"
                        />
                        <Button onClick={handleSubscribe} className="inline-flex items-center justify-center gap-2">
                            Subscribe <Send size={16} />
                        </Button>
                    </form>
                </div>
            </Section>

            {/* Pagination */}
            <Section className="bg-gray-50 py-12 pb-20">
                <div className="flex items-center justify-center gap-2">
                    <button className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-[#bebd19] hover:text-gray-900 transition-colors flex items-center gap-1">
                        <ChevronLeft size={16} /> Previous
                    </button>
                    {[1, 2, 3].map((page) => (
                        <button
                            key={page}
                            className={`w-10 h-10 rounded-lg text-sm font-semibold transition-colors ${
                                page === 1
                                    ? 'bg-[#bebd19] text-gray-900'
                                    : 'bg-white border border-gray-200 text-gray-500 hover:border-[#bebd19]'
                            }`}
                        >
                            {page}
                        </button>
                    ))}
                    <button className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-[#bebd19] hover:text-gray-900 transition-colors flex items-center gap-1">
                        Next <ChevronRight size={16} />
                    </button>
                </div>
            </Section>
        </>
    );
};

export default Blog;
